/* eslint-disable  @typescript-eslint/no-explicit-any */
import moment from "moment";
import eventBus from "./EventBus";
import ErrorReporter from "./ErrorReporter";

export interface ISendRequestParameters {
    path: string;
    postBody: any;
    resolve: (data: any) => void;
    reject: (error: any) => void;
    busyEventName?: string;
    isJson?: boolean;
}

module ApiClient {

    const bases = document.getElementsByTagName("base");
    export let baseUrl = bases.length > 0 ? bases[0].getAttribute("href") : "/";
    
    if(process.env.NODE_ENV === "development") {
        baseUrl = "http://localhost:58878/";
    }

    // recurse through response objects looking for ISO 8601 dates - and parse them
    export function processDates(data: any) {
        for (const item in data) {
            if (Object.prototype.hasOwnProperty.call(data, item)) {
                if (data[item] !== null) {
                    const type: string = typeof data[item];
                    if (type === "string") {
                        const txt = data[item] as string;
                        // seven or eight digit numbers are actually valid ISO 8601 formats - but we want to ignore those
                        if (isNaN(+txt)) {
                            // if(item === "created") {
                            //     console.log("...");
                            // }
                            const m = moment(txt, moment.ISO_8601, true);
                            if (m.isValid()) {
                                if (txt.substring(0, 4) === "0001") {
                                    data[item] = new Date(-62135596800000); // 01/01/0001 means 'no value'
                                }
                                data[item] = m.toDate(); // could leave as moment?
                            }
                        }
                    }
                    else if (type === "object") {
                        processDates(data[item]);
                    }
                }
            }
        }
    }

    export function resolveUrl(path: string): string {
        let url = baseUrl || "/";
        if(!url.endsWith("/")) url += "/";
        url += path.startsWith("/") ? path.substr(1) : path;
        return url;
    }

    export function sendRequest(parameters: ISendRequestParameters) {
        const url = resolveUrl(parameters.path);

        if(parameters.busyEventName) eventBus.$emit(parameters.busyEventName, true);

        fetch(url, {
            method: parameters.postBody ? "POST" : "GET",
            credentials: "include", // can also be set to 'omit' or 'same-origin'
            body: parameters.postBody
        }).then((response: Response) => {
            if (response.status === 401) {
                // Assume auth token has expired and emit an event to show the sign-in dialogue
                // (request will be re-issued using the parameters after successful sign-in.)
                
                if(parameters.busyEventName) eventBus.$emit(parameters.busyEventName, false);

                eventBus.$emit("http-401", parameters); // trigger sign-in dialogue
                // note that we're NOT rejecting or resolving the promise at this stage!
                return null;
            }
            else if (response.status < 200 || response.status >= 300) {
                eventBus.$emit("http-error", response); // global handler (show message box or whatever)

                if(parameters.busyEventName) eventBus.$emit(parameters.busyEventName, false);

                parameters.reject(new Error("HTTP error " + response.status)); // handled where called
                return null;
            }
            else if (response.status === 204) {
                // empty response - this would cause json to fail so just return null
                console.log("Response 204 - no content");
                return null;
            }
            else if(response.headers.get("Content-Type")?.startsWith("application/json")) {
                return response.json();
            }
            else {
                // this is for file downloads...
                return response.blob();
            }
        }).then((responseData: any) => {
            if (typeof responseData === "object" && responseData !== null && parameters.isJson) {
                processDates(responseData);
            }
            else {
                console.log("#### do not process dates");
            }
            if(parameters.busyEventName) eventBus.$emit(parameters.busyEventName, false);
            parameters.resolve(responseData);
        }).catch((reason: any) => {
            const isOnline = navigator.onLine;
            if (parameters.busyEventName) eventBus.$emit(parameters.busyEventName, false);
            ErrorReporter.apiClientErrorHandler(reason, parameters, isOnline);
            if (isOnline) {
                eventBus.$emit("fetch-exception", reason);
            }
            else {
                eventBus.$emit("fetch-failed-offline");
            }
            parameters.reject(reason);
        });
    }

    function sendRequestPromise(path: string, postBody: any, busyEventName?: string , isJson?: boolean): Promise<any> {
        return new Promise((resolve: (data: any) => void, reject: (error: any) => void) => {
            sendRequest({ path, postBody, resolve, reject, busyEventName, isJson });
        });
    }

    export function get(path: string, busyEventName?: string): Promise<any> {
        return sendRequestPromise(path, null, busyEventName, true);
    }

    export function post(path: string, data: any, busyEventName?: string): Promise<any> {
        if(!(data instanceof FormData) && typeof data !== "string") {
            data = JSON.stringify(data);
        }
        return sendRequestPromise(path, data, busyEventName, true);
    }

    export function getNonJson(path: string, busyEventName?: string): Promise<any> {
        return sendRequestPromise(path, null, busyEventName, false);
    }

    export function postNonJson(path: string, data: any, busyEventName?: string): Promise<any> {
        return sendRequestPromise(path, data, busyEventName, false);
    }
}

export default ApiClient;